/**
 * Global toast de-duplication.
 *
 * The app installs a TanStack Query `MutationCache` (see `src/main.tsx`) that
 * shows a generic success/error toast for every mutation. Many pages also show
 * their own, more specific toast for the same action, which produced two
 * stacked toasts ("Contact details saved" + "Saved successfully").
 *
 * This module records when *any* toast was last shown, so the global cache can
 * defer its own generic toast and skip it when the calling page already gave
 * feedback. It is intentionally kind-agnostic: a page that reports a partial
 * success with `toast.warning` (for example "moved 3, skipped 1") must also
 * suppress the generic success toast, otherwise the user sees a warning and a
 * success at the same time.
 *
 * It patches the shared `toast` object from `sonner`, so every current and
 * future call site is observed automatically regardless of how it imports the
 * library — no page has to remember an opt-out flag.
 *
 * The record is only ever *read* by the global cache; it deliberately is not
 * reset after a generic toast. That way a burst of mutations (for example a
 * bulk action that loops an endpoint) still collapses into a single toast even
 * when the burst outlives the dedup window — the stable sonner ids used by the
 * cache provide the same guarantee within a single tick.
 */
import { toast } from 'sonner';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

/** How long after a page toast the generic mutation toast is suppressed. */
const DEDUP_WINDOW_MS = 600;

interface DedupState {
  installed: boolean;
  lastToastAt: number;
}

// Stored on globalThis so the guard survives Vite HMR without double-wrapping
// sonner's toast methods.
const globalScope = globalThis as typeof globalThis & {
  __faToastDedup?: DedupState;
};

const state: DedupState = (globalScope.__faToastDedup ??= {
  installed: false,
  lastToastAt: 0,
});

/** True when any toast was shown within the dedup window. */
export function hasRecentToast(): boolean {
  return Date.now() - state.lastToastAt < DEDUP_WINDOW_MS;
}

type ToastMethod = typeof toast.success;

/**
 * Wrap sonner's toast methods once so page-level toasts are recorded. Sonner's
 * methods are bound arrow functions, so detaching them from `toast` is safe.
 */
export function installToastDedup(): void {
  if (state.installed) return;
  state.installed = true;

  const originals: Record<ToastKind, ToastMethod> = {
    success: toast.success,
    error: toast.error,
    info: toast.info,
    warning: toast.warning,
  };

  const wrap = (kind: ToastKind): ToastMethod => {
    const original = originals[kind];
    const wrapped: ToastMethod = (message, data) => {
      state.lastToastAt = Date.now();
      return original(message, data);
    };
    return wrapped;
  };

  toast.success = wrap('success');
  toast.error = wrap('error');
  toast.info = wrap('info');
  toast.warning = wrap('warning');
}
